import React from 'react'
import {Link} from 'react-router-dom'
import {useSearchParams} from 'react-router-dom'
import LeaseDetail from './LeaseDetail'
import {useGetLeaseUnitDetailQuery} from '../../services/residentPortalApi'
import {LeaseApplication, LeaseApplicationType} from '../../types/app-types'
import AppLayout from '../layout/AppLayout'
import Alert from '../common/Alert'
import {
  formatAmount,
  isNumeric,
  parseJwtPayload,
  parseNumber
} from '../../services/utils'

function queryStringToLeaseParams(
  searchParams: URLSearchParams
): LeaseApplication {
  const token = searchParams.get('token')
  if (token) {
    const payload = parseJwtPayload(token)
    return {
      applicationType: payload.type,
      applicationPrimaryId: payload.primaryId,
      leasePropertyId: payload.propertyId,
      leaseUnitId: payload.unitId,
      leaseStartDate: payload.leaseStart,
      leaseEndDate: payload.leaseEnd,
      leaseMonthlyRent: payload.monthlyRent
    }
  } else {
    return {
      applicationType: searchParams.get('type'),
      applicationPrimaryId: searchParams.get('primaryId'),
      leasePropertyId: searchParams.get('propertyId'),
      leaseUnitId: searchParams.get('unitId'),
      leaseStartDate: searchParams.get('leaseStart'),
      leaseEndDate: searchParams.get('leaseEnd')
    }
  }
}

function formatNewLeaseApplicationUrl(searchParams: URLSearchParams): string {
  let queryParams = []
  for (const [k, v] of searchParams.entries()) {
    queryParams.push(`${k}=${v}`)
  }
  const queryString = queryParams.join('&')
  return `/new-lease-application?${queryString}`
}

const LeaseOverviewPage = () => {
  const [searchParams] = useSearchParams()
  const url = formatNewLeaseApplicationUrl(searchParams)
  const leaseApplication = queryStringToLeaseParams(searchParams)
  const {leasePropertyId, leaseUnitId} = leaseApplication

  const getLeaseUnitDetailQuery = useGetLeaseUnitDetailQuery(
    {
      propertyId: leasePropertyId,
      unitId: leaseUnitId
    },
    {skip: !leasePropertyId || !leaseUnitId}
  )
  const leaseUnitDetail = getLeaseUnitDetailQuery?.data
  const isCoApplicant =
    leaseApplication?.applicationType === LeaseApplicationType.CoApplicant

  const applicationFee = leaseUnitDetail?.applicationFee
    ? parseNumber(leaseUnitDetail?.applicationFee)
    : 0
  const hasAdminFee = !isCoApplicant && isNumeric(leaseUnitDetail?.adminFee)
  const adminFee = hasAdminFee ? parseNumber(leaseUnitDetail?.adminFee) : 0

  return (
    <AppLayout>
      <LeaseDetail
        className="my-4"
        leaseApplication={leaseApplication}
        leaseUnitDetail={leaseUnitDetail}
      />

      <div className="card-default w-full">
        <div className="card-body-default">
          <div className="flex-grow">
            <h1 className="card-header">Lease Application Overview</h1>
            {isCoApplicant ? (
              <Alert type="info" className="mt-2 mb-6">
                <p>
                  You have been invited as a co-applicant on this lease
                  application. Please complete the lease application steps
                  outlined below.
                </p>
              </Alert>
            ) : (
              ''
            )}

            <h2 className="text-lg">1. Login or Create an Account</h2>
            <p className="ml-5">
              If you already have a Placemakr account, you can simply login.
              Otherwise, we ask that you create an account so that we can keep
              your personal information secure and make it easy for you to
              retrieve your lease applications from a central location.
            </p>
            <h2 className="text-lg mt-4">2. Complete the Lease Application</h2>
            <p className="ml-5">
              You will be required to complete an application for the unit that
              you intend to lease. You will need to provide information about
              yourself including: contact information, proof of identity,
              employment, residence, credit history, criminal history, pets,
              vehicles, etc.
            </p>
            {hasAdminFee && adminFee > 0 ? (
              <>
                <h2 className="text-lg mt-4">3. Pay Application Fees</h2>
                <p className="ml-5">
                  A one-time application fee of
                  <span className="font-bold mx-1">
                    {formatAmount(applicationFee)}
                  </span>
                  and an administrative fee of
                  <span className="font-bold mx-1">
                    {formatAmount(adminFee)}
                  </span>
                  are required at the time of applying for the lease. The
                  application fee is charged per applicant, covering the cost of
                  background and credit checks incurred by Placemakr, and is
                  non-refundable. The administrative fee, required per apartment
                  or lease, covers administrative costs associated with move-in
                  and is only refundable if your application is rejected.
                  Acceptable payment methods include credit card, debit card,
                  and direct debit (ACH).
                </p>
              </>
            ) : (
              <>
                <h2 className="text-lg mt-4">3. Pay Application Fees</h2>
                <p className="ml-5">
                  A one-time application fee of
                  <span className="font-bold mx-1">
                    {formatAmount(applicationFee)}
                  </span>
                  is required at the time of applying for the lease. The
                  application fee is charged per applicant, covering the cost of
                  background and credit checks incurred by Placemakr, and is
                  non-refundable. Acceptable payment methods include credit
                  card, debit card, and direct debit (ACH).
                </p>
              </>
            )}
            <h2 className="text-lg mt-4">4. Background and Credit Check</h2>
            <p className="ml-5">
              You will be required to provide information needed to perform a
              background and credit check.
            </p>
            <h2 className="text-lg mt-4">5. Review Application Results</h2>
            <p className="ml-5">
              The results of your lease application will be shared with you by a
              member of our team.
            </p>
          </div>
          <div className="card-actions flex-none mt-8 flex flex-row justify-end">
            <Link to={url} className="btn btn-primary">
              Start Lease Application
            </Link>
          </div>
        </div>
      </div>
    </AppLayout>
  )
}

export default LeaseOverviewPage
